import { getInsuranceNamesForCard } from 'app/ProviderDetails/AcceptedInsurance/useInsuranceSplit'
import { carrierInsuranceMatcherConfig } from 'app/ProviderDetails/AcceptedInsurance/helpers'
import {
  AvailabilityFilterValues,
  CARD_LIST_MORE_TEXT,
  CARD_LIST_VISIBLE_COUNT,
} from 'app/ProviderSearch/constants'
import { ProviderFilterTypes } from 'dataSource/ProviderSearch'
import useAppSelector from 'hooks/useAppSelector'
import { useMemo } from 'react'
import { getFormattedPracticeAddress } from 'utils/common'

type ProviderListingDetailsReturnType = {
  availabilityTags: string[]
  practiceLocation: string
  areaOfConcernValues: string[]
  allAreasOfConcernValues: string[]
  insuranceNamesValues: string[]
}

const withMoreText = (values: string[]): string[] =>
  values.length > CARD_LIST_VISIBLE_COUNT
    ? [...values.slice(0, CARD_LIST_VISIBLE_COUNT), CARD_LIST_MORE_TEXT]
    : values

const useProviderListingDetails = (
  providerDetails: Provider,
  isSupportUser?: boolean
): ProviderListingDetailsReturnType => {
  const { globalGroupedFilters } = useAppSelector((state) => state.global)
  const availabilityTypeFilters =
    globalGroupedFilters[ProviderFilterTypes.AVAILABILITY_TYPE]
  const areaOfConcernFilters =
    globalGroupedFilters[ProviderFilterTypes.AREA_OF_CONCERN]
  const { availabilityTypeIds, areaOfConcernIds, insuranceNames, practice } =
    providerDetails ?? {}

  const practiceLocation = isSupportUser
    ? getFormattedPracticeAddress(practice).address
    : `${practice.city}, ${practice.state}`

  // As per https://thriveworks.atlassian.net/browse/THRIV-3528
  // Hiding the intro text, but it could come in later tickets
  // const introText = getTextFromHtml(providerSummary || shortIntro || '')

  //------------------------------------ Insurance section data handling
  const insuranceNamesValues = withMoreText(
    getInsuranceNamesForCard(
      insuranceNames?.map((insuranceName) => ({
        insuranceProvider: {
          name: insuranceName,
        },
      })),
      carrierInsuranceMatcherConfig
    )
  )
  //----------------------------------------------------------

  const availabilityTags: string[] = useMemo(
    () =>
      availabilityTypeIds?.reduce((accumulator, availabilityId) => {
        const availabilityFilter = availabilityTypeFilters.find(
          (availabilityFilter) => availabilityFilter.id === availabilityId
        )

        if (availabilityFilter?.value === AvailabilityFilterValues.inPerson) {
          accumulator.push('in-person')
        } else if (
          availabilityFilter?.value === AvailabilityFilterValues.online
        ) {
          accumulator.push('video')
        }

        return accumulator
      }, []) ?? [],
    [availabilityTypeIds, availabilityTypeFilters]
  )

  const [areaOfConcernValues, allAreasOfConcernValues] = useMemo(() => {
    const areaOfConcernvalues = areaOfConcernIds?.reduce(
      (accumulator, areaOfConcernId) => {
        const availabilityFilter = areaOfConcernFilters.find(
          (areaOfConcernFilter) => areaOfConcernFilter.id === areaOfConcernId
        )

        if (availabilityFilter) {
          accumulator.push(availabilityFilter.value)
        }

        return accumulator
      },
      []
    )

    return [withMoreText(areaOfConcernvalues ?? []), areaOfConcernvalues ?? []]
  }, [areaOfConcernFilters, areaOfConcernIds])

  return {
    availabilityTags,
    practiceLocation,
    areaOfConcernValues,
    allAreasOfConcernValues,
    insuranceNamesValues,
  }
}

export default useProviderListingDetails
